/**
 * StateManager - Lightweight reactive state management
 * Provides get/set/subscribe for centralized app state.
 */
export class StateManager {
  constructor(defaults = {}) {
    this._state = { ...defaults };
    this._listeners = new Map(); // key -> Set<callback>
    this._globalListeners = new Set();
  }

  get(key) {
    return this._state[key];
  }

  set(key, value) {
    const old = this._state[key];
    if (old === value) return;
    this._state[key] = value;

    // Notify key-specific listeners
    const listeners = this._listeners.get(key);
    if (listeners) {
      listeners.forEach(cb => {
        try { cb(value, old, key); }
        catch (e) { console.error(`State listener error [${key}]:`, e); }
      });
    }

    // Notify global listeners
    this._globalListeners.forEach(cb => {
      try { cb(key, value, old); }
      catch (e) { console.error('Global state listener error:', e); }
    });
  }

  subscribe(key, callback) {
    if (!this._listeners.has(key)) {
      this._listeners.set(key, new Set());
    }
    this._listeners.get(key).add(callback);
    return () => this._listeners.get(key)?.delete(callback);
  }

  onAny(callback) {
    this._globalListeners.add(callback);
    return () => this._globalListeners.delete(callback);
  }

  getAll() {
    return { ...this._state };
  }
}
